import {
  getTouristsTextActualiz,
  getAgeText,
  getOrderStage,
} from './CommonUtils';
import { getFlightToText, getFlightBackText } from './FlightUtils';
import { formatSlashes } from './DateUtil';
import { getPrice } from './Constants/PriceUtil';
import {
  PROFILE_ORDER_TOURIST,
  TOURIST_CHILD,
} from './Constants/GlobalConstants';
import { STAGE_ORDER_CREATED } from './Constants/AdminConstants';

export function getTouristsCount(acualizationData) {
  return (
    parseInt(acualizationData.adults, 10) + parseInt(acualizationData.child, 10)
  );
}

export function getSplitPrice(totalValue, splitPrice) {
  return Math.ceil(totalValue * (splitPrice / 100));
}

// the operator price is given for the default flight only:
// another flight changes it in proportion to the tour price
function getOperatorPrice(acualizationData, selectedFlight, tourPriceValue) {
  const { operatorprice, price, currency } = acualizationData;
  const operatorPrice = parseFloat(operatorprice);
  const defaultPrice = parseInt(price, 10);

  if (
    !operatorPrice ||
    !defaultPrice ||
    !tourPriceValue ||
    tourPriceValue === defaultPrice ||
    !selectedFlight.price ||
    selectedFlight.price.currency !== currency
  ) {
    return operatorprice;
  }

  return ((operatorPrice * tourPriceValue) / defaultPrice).toFixed(2);
}

// the order sent to the backend, built from the actualized tour
export function getOrderPayload(params) {
  const {
    acualizationData,
    selectedFlight,
    tourInfo,
    insuranceChecked,
    promoCode,
    isPromoApplied,
    splitPrice,
    priceCurrency,
    tourId,
    phoneNumber,
    email,
    note,
  } = params;

  const { totalPrice, totalValue, insurancePriceValue, tourPriceValue } =
    getPrice(
      acualizationData,
      selectedFlight,
      tourInfo,
      insuranceChecked,
      promoCode,
    );
  const splitPaymentValue = getSplitPrice(totalValue, splitPrice);

  return {
    phoneNumber,
    email,

    operator: acualizationData.operatorname,
    originalPrice: getOperatorPrice(
      acualizationData,
      selectedFlight,
      tourPriceValue,
    ),

    totalPrice,
    totalPriceByn: totalValue,
    totalPriceAlfa: totalValue * 100, // for add .00 at the end
    currency: acualizationData.operatorcurrency,

    departureCity: acualizationData.departurename,
    countryTo: acualizationData.countryname,
    resort: acualizationData.hotelregionname,
    program: acualizationData.tourname,

    hotelId: acualizationData.hotelcode,
    hotelName: acualizationData.hotelname,
    hotelCategory: acualizationData.hotelstars,
    meal: acualizationData.meal,
    roomType: acualizationData.room,
    settlementType: acualizationData.placement,

    nightsCount: acualizationData.nights,
    departureDate: acualizationData.flydate,

    flightInsurance: insuranceChecked,
    flightInsuranceValueByn: insurancePriceValue,

    toTrip: getFlightToText(selectedFlight),
    backTrip: getFlightBackText(selectedFlight),

    adultsCount: acualizationData.adults,
    kidsCount: acualizationData.child,
    childAge1: acualizationData.childage1,
    childAge2: acualizationData.childage2,
    childAge3: acualizationData.childage3,

    touristsText: getTouristsTextActualiz(acualizationData),
    touristsCount: getTouristsCount(acualizationData),

    split: splitPrice,
    splitPayment: `${splitPaymentValue} ${priceCurrency}`,
    splitPaymentValue,
    splitPaymentAlfa: splitPaymentValue * 100,

    note,
    tourId,
    hotelImage: acualizationData.hotelpicturebig,
    promoCode: isPromoApplied ? promoCode.name : '',
  };
}

const getChildAges = (order) => {
  const kidsCount = parseInt(order.kidsCount, 10) || 0;

  return Array.from({ length: kidsCount }, (item, index) =>
    parseInt(order[`childAge${index + 1}`], 10));
};

// a tour is replaced only with the same adults, children and children ages
export function isSameTourists(order, newOrder) {
  return (
    (parseInt(order.adultsCount, 10) || 0) ===
      (parseInt(newOrder.adultsCount, 10) || 0) &&
    getChildAges(order).join() === getChildAges(newOrder).join()
  );
}

// buyer and tourists forms: checkout and the user cabinet --------------------

export function getEmptyContractInfo() {
  return {
    lastName: '',
    lastNameValid: false,
    firstName: '',
    firstNameValid: false,
    afterName: '',
    afterNameValid: false,
    birthDay: '',
    birthDayValid: false,
    passport: '',
    passportValid: false,
    passportStartDate: '',
    passportStartDateValid: false,
    passportEndDate: '',
    passportEndDateValid: false,
    sex: '',
    sexValid: false,
    postIndex: '',
    postIndexValid: false,
    city: '',
    cityValid: false,
    street: '',
    streetValid: false,
    house: '',
    houseValid: false,
    flat: '',
  };
}

export function getEmptyTourists(order) {
  const totalCount =
    parseInt(order.adultsCount, 10) + parseInt(order.kidsCount, 10);

  return Array.from({ length: totalCount }, (item, index) => ({
    id: index,
    lastNameUpper: '',
    lastNameUpperValid: false,
    firstNameUpper: '',
    firstNameUpperValid: false,
    birthDay: '',
    birthDayValid: false,
    citizenship: 'Беларусь',
    citizenshipValid: true,
    passport: '',
    passportValid: false,
    passportStartDate: '',
    passportStartDateValid: false,
    passportEndDate: '',
    passportEndDateValid: false,
    identity: '',
    identityValid: false,
    sex: '',
    sexValid: false,
  }));
}

function isPostalAddressValid(contractInfo) {
  return (
    contractInfo.postIndexValid &&
    contractInfo.cityValid &&
    contractInfo.streetValid &&
    contractInfo.houseValid &&
    [contractInfo.postIndex, contractInfo.city, contractInfo.street]
      .concat(contractInfo.house)
      .every((value) => value.trim().length > 0)
  );
}

export function isContractInfoValid(contractInfo) {
  return (
    contractInfo.lastNameValid &&
    contractInfo.firstNameValid &&
    contractInfo.afterNameValid &&
    contractInfo.birthDayValid &&
    contractInfo.passportValid &&
    contractInfo.passportStartDateValid &&
    contractInfo.passportEndDateValid &&
    contractInfo.sexValid &&
    isPostalAddressValid(contractInfo)
  );
}

export function isTouristsValid(tourists) {
  return tourists.every(
    (tourist) =>
      tourist.lastNameUpperValid &&
      tourist.firstNameUpperValid &&
      tourist.birthDayValid &&
      tourist.citizenshipValid &&
      tourist.passportValid &&
      tourist.passportStartDateValid &&
      tourist.passportEndDateValid &&
      tourist.identityValid &&
      tourist.sexValid,
  );
}

export function getContactInfoPayload(contractInfo) {
  return {
    firstName: contractInfo.firstName,
    lastName: contractInfo.lastName,
    afterName: contractInfo.afterName,
    birthDay: formatSlashes(contractInfo.birthDay),
    passport: contractInfo.passport,
    passportStartDate: formatSlashes(contractInfo.passportStartDate),
    passportEndDate: formatSlashes(contractInfo.passportEndDate),
    sex: contractInfo.sex,
    postIndex: contractInfo.postIndex.trim(),
    city: contractInfo.city.trim(),
    street: contractInfo.street.trim(),
    house: contractInfo.house.trim(),
    flat: contractInfo.flat.trim(),
  };
}

export function getTouristsPayload(tourists) {
  return tourists.map((tourist) => ({
    lastNameUpper: tourist.lastNameUpper,
    firstNameUpper: tourist.firstNameUpper,
    birthDay: formatSlashes(tourist.birthDay),
    sex: tourist.sex,
    citizenship: tourist.citizenship,
    passport: tourist.passport,
    passportStartDate: formatSlashes(tourist.passportStartDate),
    passportEndDate: formatSlashes(tourist.passportEndDate),
    identity: tourist.identity,
  }));
}

// adults go first, then children: "Турист 3 — ребенок, 5 лет"
export function getTouristTitle(order, index) {
  const title = `${PROFILE_ORDER_TOURIST} ${index + 1}`;
  const adultsCount = parseInt(order.adultsCount, 10) || 0;

  if (index < adultsCount) {
    return title;
  }

  const age = parseInt(order[`childAge${index - adultsCount + 1}`], 10);

  return Number.isNaN(age)
    ? `${title} — ${TOURIST_CHILD}`
    : `${title} — ${TOURIST_CHILD}, ${getAgeText(age)}`;
}

// the customer fills and edits the people only while the order is just created,
// later only a manager changes them in the admin panel
export function isOrderPeopleEditable(order) {
  return (
    !!order && !order.archived && getOrderStage(order) === STAGE_ORDER_CREATED
  );
}

// an order created by a manager waits for the customer to fill the people
export function isOrderPeopleMissing(order) {
  return isOrderPeopleEditable(order) && !order.contactInfo;
}

const withValidFields = (person, keys) =>
  keys.reduce(
    (result, key) => ({
      ...result,
      [key]: person[key] || '',
      [`${key}Valid`]: !!person[key],
    }),
    {},
  );

// saved people as the forms state, to edit them
export function getContractInfoFromPerson(person) {
  return {
    ...getEmptyContractInfo(),
    ...withValidFields(person, [
      'lastName',
      'firstName',
      'afterName',
      'birthDay',
      'passport',
      'passportStartDate',
      'passportEndDate',
      'sex',
      'postIndex',
      'city',
      'street',
      'house',
    ]),
    flat: person.flat || '',
  };
}

export function getTouristsFromOrder(order) {
  const emptyTourists = getEmptyTourists(order);
  const saved = order.tourists || [];

  return emptyTourists.map((tourist, index) =>
    (saved[index]
      ? {
        ...tourist,
        ...withValidFields(saved[index], [
          'lastNameUpper',
          'firstNameUpper',
          'birthDay',
          'citizenship',
          'passport',
          'passportStartDate',
          'passportEndDate',
          'identity',
          'sex',
        ]),
      }
      : tourist));
}
